/**
 * Component Order Utilities
 * Shared utilities for building and managing component ordering
 */

import {
    IPoll,
    ITextFieldCollection,
    IChannelComponentOrderItem,
} from "@Interfaces";

export interface ComponentItem {
    id: string;
    type: "poll" | "textfield";
    title: string;
    question?: string;
}

export type PollsTabItem =
    | { type: "poll"; poll: IPoll }
    | { type: "textfield"; textField: ITextFieldCollection };

/**
 * Extract IDs from reordered items in the correct order, separated by type
 */
export const extractIdsByType = (
    items: ComponentItem[]
): { pollIds: string[]; textFieldIds: string[] } => {
    const pollIds: string[] = [];
    const textFieldIds: string[] = [];

    items.forEach((item) => {
        if (item.type === "poll") {
            pollIds.push(item.id);
        } else {
            textFieldIds.push(item.id);
        }
    });

    return { pollIds, textFieldIds };
};

/**
 * Create a ComponentItem from a Poll
 */
export const createComponentItemFromPoll = (poll: IPoll): ComponentItem => ({
    id: poll._id,
    type: "poll",
    title: poll.question,
});

/**
 * Create a ComponentItem from a TextField
 */
export const createComponentItemFromTextField = (
    textField: ITextFieldCollection
): ComponentItem => ({
    id: textField._id,
    type: "textfield",
    title: textField.question,
    question: textField.question,
});

/**
 * Build a list of component items from polls and text fields,
 * respecting the provided component order if available.
 *
 * Items specified in the order are placed first in their specified order.
 * Remaining items are appended at the end.
 *
 * @param polls - Array of polls
 * @param textFields - Array of text fields
 * @param order - Optional component order specification
 * @returns Array of component items in the correct order
 */
export const buildComponentItems = (
    polls: IPoll[],
    textFields: ITextFieldCollection[],
    order?: IChannelComponentOrderItem[]
): ComponentItem[] => {
    const pollMap = new Map(polls.map((poll) => [poll._id, poll]));
    const textFieldMap = new Map(
        textFields.map((textField) => [textField._id, textField])
    );

    const orderedItems: ComponentItem[] = [];

    // Process items in the specified order
    (order ?? []).forEach((entry) => {
        if (entry.type === "poll") {
            const poll = pollMap.get(entry.id);
            if (poll) {
                orderedItems.push(createComponentItemFromPoll(poll));
                pollMap.delete(entry.id);
            }
            return;
        }

        const textField = textFieldMap.get(entry.id);
        if (textField) {
            orderedItems.push(createComponentItemFromTextField(textField));
            textFieldMap.delete(entry.id);
        }
    });

    // Append remaining items
    const remainingPolls = Array.from(pollMap.values());
    const remainingTextFields = Array.from(textFieldMap.values()).sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );

    orderedItems.push(
        ...remainingPolls.map(createComponentItemFromPoll),
        ...remainingTextFields.map(createComponentItemFromTextField)
    );

    return orderedItems;
};

/**
 * Build PollsTabItem array from polls and text fields,
 * respecting the provided component order if available.
 *
 * This is used by the Home page to display items in the correct order.
 *
 * @param polls - Array of polls
 * @param textFields - Array of text fields
 * @param order - Optional component order specification
 * @returns Array of PollsTabItem in the correct order
 */
export const buildPollsTabItems = (
    polls: IPoll[],
    textFields: ITextFieldCollection[],
    order?: IChannelComponentOrderItem[]
): PollsTabItem[] => {
    const pollMap = new Map(polls.map((poll) => [poll._id, poll]));
    const textFieldMap = new Map(
        textFields.map((textField) => [textField._id, textField])
    );
    const orderedItems: PollsTabItem[] = [];

    // Process items in the specified order
    (order ?? []).forEach((entry) => {
        if (entry.type === "poll") {
            const poll = pollMap.get(entry.id);
            if (poll) {
                orderedItems.push({ type: "poll", poll });
                pollMap.delete(entry.id);
            }
            return;
        }

        const textField = textFieldMap.get(entry.id);
        if (textField) {
            orderedItems.push({ type: "textfield", textField });
            textFieldMap.delete(entry.id);
        }
    });

    // Append remaining items
    orderedItems.push(
        ...Array.from(pollMap.values()).map((poll) => ({
            type: "poll" as const,
            poll,
        })),
        ...Array.from(textFieldMap.values())
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((textField) => ({
                type: "textfield" as const,
                textField,
            }))
    );

    return orderedItems;
};
